<script setup lang="ts">
import { ConsentBanner } from '@hatfieldmedia/hmcore'
import { RouterView } from 'vue-router'
</script>

<template>
  <!-- Background decorations -->
  <!-- <div class="bg-grid" aria-hidden="true"></div>
  <div class="bg-diag" aria-hidden="true"></div> -->
  <div class="bg-orb orb1" aria-hidden="true"></div>
  <div class="bg-orb orb2" aria-hidden="true"></div>

  <RouterView />
  <ConsentBanner />
</template>

<style global>
@font-face {
  font-family: 'HND';
  src: url('/fonts/HND-400.otf') format('opentype');
  font-weight: 400;
  font-display: swap;
}

@font-face {
  font-family: 'HND';
  src: url('/fonts/HND-900.otf') format('opentype');
  font-weight: 900;
  font-display: swap;
}

@font-face {
  font-family: 'HND';
  src: url('/fonts/HND-950.otf') format('opentype');
  font-weight: 950;
  font-display: swap;
}

@font-face {
  font-family: 'HNDC';
  src: url('/fonts/HNDC-700.otf') format('opentype');
  font-weight: 700;
  font-display: swap;
}

@font-face {
  font-family: 'HNDC';
  src: url('/fonts/HNDC-900.otf') format('opentype');
  font-weight: 900;
  font-display: swap;
}

@font-face {
  font-family: 'HNT';
  src: url('/fonts/HNT-400.otf') format('opentype');
  font-weight: 400;
  font-display: swap;
}

@font-face {
  font-family: 'Jost';
  src: url('/fonts/Jost-100-900.ttf') format('truetype');
  font-weight: 100 900;
  font-display: swap;
}

:root {
  /* Brand colors */
  --pink: #BA328B;
  --pink-bright: #C8389A;
  --purple: #5F249F;
  --blue-purple: #546AA4;

  /* Backgrounds */
  --darker: #030912;
  --dark: #060f1e;
  --card: #0b1526;

  /* Text */
  --text: #c8d0dc;
  --white: #ffffff;

  /* Utility */
  --color-success: #22c55e;
  --color-error: #ef4444;
  --radius-sm: 6px;
  --radius-md: 12px;
  --radius-lg: 16px;
  --transition: 0.2s ease;
}

*,
*::before,
*::after {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html {
  scroll-behavior: smooth;
  -webkit-text-size-adjust: 100%;
}

body {
  font-family:
    'HND',
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    Roboto,
    sans-serif;
  color: var(--text);
  background: var(--darker);
  overflow-x: hidden;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

img {
  display: block;
  max-width: 100%;
  height: auto;
}

h1,
h2,
h3,
h4,
h5,
h6 {
  text-wrap: balance;
}

div,
p,
span {
  text-wrap: pretty;
}

button {
  a {
    color: inherit;
    text-decoration: none;
  }
}

/* ─── Font family aliases ─── */
/* Helvetica Now Display — heading / display font (commercial; fallback to system) */
/* Helvetica Now Text — body font (commercial; fallback to Jost / system) */
/* Helvetica Now Display Condensed — condensed headings (commercial; fallback to system) */

/* ─── Background decorations ─── */
.bg-grid {
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.02) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.02) 1px, transparent 1px);
  background-size: 60px 60px;
}

.bg-orb {
  position: fixed;
  border-radius: 50%;
  filter: blur(120px);
  pointer-events: none;
  z-index: 0;
  opacity: 0.12;
}

.bg-orb.orb1 {
  width: 600px;
  height: 600px;
  background: var(--pink);
  top: -200px;
  left: -200px;
}

.bg-orb.orb2 {
  width: 500px;
  height: 500px;
  background: var(--purple);
  bottom: -150px;
  right: -150px;
}

.bg-diag {
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background: repeating-linear-gradient(
    135deg,
    transparent,
    transparent 120px,
    rgba(208, 23, 122, 0.015) 120px,
    rgba(208, 23, 122, 0.015) 121px
  );
}

/* ─── Shared section helpers ─── */
section {
  position: relative;
  z-index: 1;
}

.section-pad {
  padding: 100px 48px;
}

.section-label {
  font-family: 'HND', sans-serif;
  font-size: 0.75rem;
  color: var(--pink-bright);
  letter-spacing: 0.16em;
  text-transform: uppercase;
  margin-bottom: 12px;
}

.section-title {
  font-family: 'HND', system-ui, sans-serif;
  font-weight: 950;
  font-size: clamp(2rem, 4vw, 3.2rem);
  color: var(--white);
  text-transform: uppercase;
  margin-bottom: 8px;
}

.accent-line {
  width: 48px;
  height: 3px;
  background: linear-gradient(90deg, var(--pink), var(--purple));
  margin: 12px auto 0;
  border-radius: 4px;
}

.text-center {
  text-align: center;
}

/* ─── Shared button styles ─── */
.btn-primary {
  padding: 14px 36px;
  border-radius: 9999px;
  border: none;
  cursor: pointer;
  background: linear-gradient(135deg, var(--pink), var(--purple));
  color: #fff;
  font-family: 'HND', sans-serif;
  font-weight: 700;
  font-size: 0.9rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  transition:
    transform 0.2s,
    box-shadow 0.2s;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(208, 23, 122, 0.4);
}

.btn-outline {
  padding: 14px 36px;
  border-radius: 9999px;
  cursor: pointer;
  background: transparent;
  border: 2px solid rgba(255, 255, 255, 0.25);
  color: #fff;
  font-family: 'HND', sans-serif;
  font-weight: 600;
  font-size: 0.9rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  transition: all 0.25s;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}

.btn-outline:hover {
  border-color: var(--pink-bright);
  color: var(--pink-bright);
}

/* ─── Responsive ─── */
@media (max-width: 768px) {
  .section-pad {
    padding: 60px 20px;
  }
}
</style>
